import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown, Modal } from "react-bootstrap";
import {
  getStudentProfiles,
  createStudentProfile,
  updateStudentProfile,
  deleteStudentProfile,
} from "../../../services/StudentProfileService";
import { getUsers } from "../../../services/UserService";
import { getClasses } from "../../../services/ClassService";
import { getLevels } from "../../../services/LevelService";
import DataTablePagination from "../common/DataTablePagination";
import { Trans, useTranslation } from "react-i18next";
import { usePermissions, PERMISSIONS } from "../../../utils/permissions";

import i18n from "../../../i18n";
import { getLocale } from "../../../utils/i18nLabels";
const STATUS_OPTIONS = [
  { value: "active", get label() { return i18n.t("common.crud.statusActive"); }, badge: "success" },
  { value: "repeating", get label() { return i18n.t("pages.students.redoublant"); }, badge: "warning" },
  { value: "suspended", get label() { return i18n.t("pages.students.suspendu"); }, badge: "secondary" },
  { value: "excluded", get label() { return i18n.t("pages.students.exclu"); }, badge: "danger" },
  { value: "graduated", get label() { return i18n.t("pages.students.diplome"); }, badge: "info" },
];

const statusMeta = (value) =>
  STATUS_OPTIONS.find((s) => s.value === value) || { label: value || "-", badge: "secondary" };

const emptyForm = {
  accountMode: "new", // "new" = créer le compte en même temps | "existing" = attacher un compte déjà créé
  user_id: "",
  email: "",
  password: "",
  first_name: "",
  last_name: "",
  phone: "",
  nni: "",
  gender: "",
  date_of_birth: "",
  place_of_birth: "",
  nationality: "",
  matricule: "",
  class_id: "",
  level_id: "",
  status: "active",
  preferred_language: "fr",
  accessibility_extra_time_minutes: 0,
  training_type: "",
  is_scholarship: false,
  is_transferred: false,
  transfer_institution: "",
  transfer_first_enrollment_date: "",
  diploma_obtained: "",
};

const Students = () => {
  const { t } = useTranslation();
  const can = usePermissions();
  const canCreate = can(PERMISSIONS.STUDENT_PROFILE_CREATE);
  const canUpdate = can(PERMISSIONS.STUDENT_PROFILE_UPDATE);
  const canDelete = can(PERMISSIONS.STUDENT_PROFILE_DELETE);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [users, setUsers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [levels, setLevels] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [viewMode, setViewMode] = useState("grid");

  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [editingId, setEditingId] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    getUsers({ page: 1, limit: 200, role: "STUDENT" }).then((res) => setUsers(res.data?.data || [])).catch(() => {});
    getClasses({ page: 1, limit: 200 }).then((res) => setClasses(res.data?.data || [])).catch(() => {});
    getLevels({ page: 1, limit: 200 }).then((res) => setLevels(res.data?.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "created_at", sort_dir: "desc" };
      if (classFilter) params.class_id = classFilter;
      if (levelFilter) params.level_id = levelFilter;
      if (statusFilter) params.status = statusFilter;

      const res = await getStudentProfiles(params);
      const body = res.data;
      setProfiles(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.students.impossibleChargerListeEtudiants"));
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, classFilter, levelFilter, statusFilter, t]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const userLabel = (userId) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return userId;
    const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
    return name ? `${name} (${user.email})` : user.email;
  };

  const classLabel = (id) => classes.find((c) => c.id === id)?.name || "-";
  const levelLabel = (id) => levels.find((l) => l.id === id)?.name || "-";

  const visibleProfiles = search
    ? profiles.filter(
        (p) =>
          userLabel(p.user_id).toLowerCase().includes(search.toLowerCase()) ||
          (p.matricule || "").toLowerCase().includes(search.toLowerCase()),
      )
    : profiles;

  const usersWithoutProfile = users.filter((u) => !profiles.some((p) => p.user_id === u.id));

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setClassFilter("");
    setLevelFilter("");
    setStatusFilter("");
    setPage(1);
  };

  const openAddModal = () => {
    setFormMode("add");
    setFormData(emptyForm);
    setFormErrors({});
    setSubmitError("");
    setEditingId(null);
    setShowFormModal(true);
  };

  const openEditModal = (profile) => {
    setFormMode("edit");
    setFormData({
      user_id: profile.user_id || "",
      matricule: profile.matricule || "",
      class_id: profile.class_id || "",
      level_id: profile.level_id || "",
      status: profile.status || "active",
      preferred_language: profile.preferred_language || "fr",
      accessibility_extra_time_minutes: profile.accessibility_extra_time_minutes ?? 0,
      training_type: profile.training_type || "",
      is_scholarship: !!profile.is_scholarship,
      is_transferred: !!profile.is_transferred,
      transfer_institution: profile.transfer_institution || "",
      transfer_first_enrollment_date: profile.transfer_first_enrollment_date
        ? profile.transfer_first_enrollment_date.substring(0, 10)
        : "",
      diploma_obtained: profile.diploma_obtained || "",
    });
    setFormErrors({});
    setSubmitError("");
    setEditingId(profile.id);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    if (submitting) return;
    setShowFormModal(false);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validateForm = () => {
    const errors = {};
    if (formMode === "add") {
      if (formData.accountMode === "existing") {
        if (!formData.user_id) errors.user_id = t("common.ui.leCompteUtilisateurEstRequis");
      } else {
        if (!formData.first_name.trim()) errors.first_name = t("common.ui.lePrenomEstRequis");
        if (!formData.last_name.trim()) errors.last_name = t("common.ui.leNomEstRequis");
        if (!formData.email.trim()) errors.email = t("common.ui.lEmailEstRequis");
        if (!formData.password || formData.password.length < 6)
          errors.password = t("common.ui.leMotPasseDoitContenirMoins");
      }
    }
    if (!formData.matricule.trim()) errors.matricule = t("pages.students.matriculeRequired");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError("");

    const extendedFields = {
      preferred_language: formData.preferred_language || "fr",
      accessibility_extra_time_minutes: Number(formData.accessibility_extra_time_minutes) || 0,
      training_type: formData.training_type || null,
      is_scholarship: formData.is_scholarship,
      is_transferred: formData.is_transferred,
      transfer_institution: formData.is_transferred ? formData.transfer_institution.trim() || null : null,
      transfer_first_enrollment_date: formData.is_transferred ? formData.transfer_first_enrollment_date || null : null,
      diploma_obtained: formData.diploma_obtained.trim() || null,
    };

    try {
      if (formMode === "add") {
        const payload =
          formData.accountMode === "existing"
            ? { user_id: formData.user_id }
            : {
                email: formData.email.trim(),
                password: formData.password,
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
                phone: formData.phone.trim() || null,
                nni: formData.nni.trim() || null,
                gender: formData.gender || null,
                date_of_birth: formData.date_of_birth || null,
                place_of_birth: formData.place_of_birth.trim() || null,
                nationality: formData.nationality.trim() || null,
              };
        await createStudentProfile({
          ...payload,
          matricule: formData.matricule.trim(),
          class_id: formData.class_id || null,
          level_id: formData.level_id || null,
          status: formData.status || "active",
          ...extendedFields,
        });
        setFeedback({
          type: "success",
          message:
            formData.accountMode === "existing"
              ? t("pages.students.createProfileSuccess")
              : t("pages.students.createAccountAndProfileSuccess"),
        });
      } else {
        await updateStudentProfile(editingId, {
          matricule: formData.matricule.trim(),
          class_id: formData.class_id || null,
          level_id: formData.level_id || null,
          status: formData.status || "active",
          ...extendedFields,
        });
        setFeedback({ type: "success", message: t("pages.students.updateSuccess") });
      }
      setShowFormModal(false);
      fetchProfiles();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || t("common.crud.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteStudentProfile(deleteTarget.id);
      setFeedback({ type: "success", message: t("pages.students.deleteSuccess") });
      setDeleteTarget(null);
      if (profiles.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchProfiles();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("common.ui.impossibleSupprimerProfil") });
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{t("menu.students.title")}</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="pe-1 mb-2">
              <button type="button" className="btn btn-outline-light bg-white btn-icon me-1" onClick={resetFilters}>
                <i className="ti ti-refresh"></i>
              </button>
            </div>
            <div className="mb-2">
              {canCreate && (
<button type="button" className="btn btn-primary d-flex align-items-center" onClick={openAddModal}>
                <i className="ti ti-square-rounded-plus me-2"></i>{t("pages.students.addLabel")}
              </button>
)}
            </div>
          </div>
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="alert alert-info">
          <Trans i18nKey="pages.students.infoBanner" components={[<strong />, <Link to="/users" />]} />
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{t("menu.students.list")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="input-icon-start mb-3 me-2 position-relative">
                <span className="icon-addon"></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.students.searchPlaceholder")}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{ paddingLeft: "35px", minWidth: "220px" }}
                />
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={classFilter}
                  onChange={(e) => {
                    setClassFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.fields.allClasses")}</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={levelFilter}
                  onChange={(e) => {
                    setLevelFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.fields.allLevels")}</option>
                  {levels.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3 me-2">
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">{t("common.crud.allStatuses")}</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="d-flex align-items-center bg-white border rounded-2 p-1 mb-3">
                <button
                  type="button"
                  className={`btn btn-icon btn-sm me-1 ${viewMode === "list" ? "bg-light" : ""}`}
                  onClick={() => setViewMode("list")}
                  title={t("common.ui.vueListe")}
                >
                  <i className="ti ti-list-tree"></i>
                </button>
                <button
                  type="button"
                  className={`btn btn-icon btn-sm ${viewMode === "grid" ? "bg-light" : ""}`}
                  onClick={() => setViewMode("grid")}
                  title={t("common.ui.vueGrille")}
                >
                  <i className="ti ti-grid-dots"></i>
                </button>
              </div>
            </div>
          </div>

          <DataTablePagination
            variant="header"
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={(v) => {
              setLimit(v);
              setPage(1);
            }}
          />

          <div className="card-body p-0 py-3">
            {viewMode === "grid" ? (
              <div className="row px-3">
                {loading ? (
                  <div className="col-12 text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">{t("common.crud.loading")}</span>
                    </div>
                  </div>
                ) : loadError ? (
                  <div className="col-12 text-center py-4 text-danger">{loadError}</div>
                ) : visibleProfiles.length > 0 ? (
                  visibleProfiles.map((profile) => {
                    const account = users.find((u) => u.id === profile.user_id);
                    const name =
                      [account?.first_name, account?.last_name].filter(Boolean).join(" ") ||
                      account?.email ||
                      profile.matricule;
                    const inactive = account?.is_active === false;
                    return (
                      <div className="col-xxl-3 col-xl-4 col-md-6 mb-4 d-flex" key={profile.id}>
                        <div className="card flex-fill mb-0 h-auto shadow-sm" style={{ minWidth: 0 }}>
                          <div className="card-header d-flex align-items-center justify-content-between">
                            <span className="fw-medium">{profile.matricule}</span>
                            <div className="d-flex align-items-center">
                              <span
                                className={`badge badge-soft-${statusMeta(profile.status).badge} d-inline-flex align-items-center me-1`}
                              >
                                <i className="ti ti-circle-filled fs-5 me-1"></i>
                                {statusMeta(profile.status).label}
                              </span>
                              <span
                                className={`badge badge-soft-${inactive ? "danger" : "success"} d-inline-flex align-items-center me-1`}
                              >
                                <i className="ti ti-circle-filled fs-5 me-1"></i>
                                {inactive ? t("common.crud.statusInactive") : t("common.crud.statusActive")}
                              </span>
                              <Dropdown>
                                <Dropdown.Toggle
                                  as="button"
                                  className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                                >
                                  <i className="ti ti-dots-vertical fs-14"></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu align="end" className="p-3">
                                  <Dropdown.Item as={Link} className="rounded-1" to={`/student/${profile.id}`}>
                                    <i className="ti ti-eye me-2"></i>{t("common.crud.viewDetails")}
                                  </Dropdown.Item>
                                  {canUpdate && (
<Dropdown.Item className="rounded-1" onClick={() => openEditModal(profile)}>
                                    <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                                  </Dropdown.Item>
)}
                                  {canDelete && (
<Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(profile)}>
                                    <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                                  </Dropdown.Item>
)}
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </div>
                          <div className="card-body">
                            <div className="bg-light rounded-2 p-3 mb-3">
                              <div className="d-flex flex-column  align-items-center">
                                <span className="avatar avatar-lg flex-shrink-0 bg-primary-transparent rounded-circle d-flex align-items-center justify-content-center">
                                  {account?.avatar_url ? (
                                    <img src={account.avatar_url} className="img-fluid rounded-circle" alt={name} />
                                  ) : (
                                    <span className="text-primary fw-semibold">
                                      {name.slice(0, 2).toUpperCase()}
                                    </span>
                                  )}
                                </span>
                                <div className="w-100 mt-2 text-center overflow-hidden">
                                  <h6 className="mb-0 text-truncate" title={name}>{name}</h6>
                                  <p className="mb-0 text-muted text-truncate">
                                    {profile.class_id ? classLabel(profile.class_id) : "-"}
                                    {profile.level_id ? ` · ${levelLabel(profile.level_id)}` : ""}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="d-flex align-items-center justify-content-between">
                              <div>
                                <p className="mb-0 text-muted">{t("common.crud.enrolledOn")}</p>
                                <p className="text-dark mb-0">
                                  {profile.created_at
                                    ? new Date(profile.created_at).toLocaleDateString(getLocale())
                                    : "-"}
                                </p>
                              </div>
                            </div>
                            <div className="mt-3">
                              <p className="mb-1 text-muted text-truncate">
                                <i className="ti ti-phone me-1"></i>
                                {account?.phone || "-"}
                              </p>
                              <p className="mb-0 text-muted text-truncate" title={account?.email || ""}>
                                <i className="ti ti-mail me-1"></i>
                                {account?.email || "-"}
                              </p>
                            </div>
                            <Link
                              to={`/student/${profile.id}`}
                              className="btn btn-outline-primary btn-sm w-100 mt-3"
                            >
                              <i className="ti ti-eye me-1"></i>{t("common.crud.viewDetail")}
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-12 text-center py-4">
                    <i className="ti ti-search-off fs-1 text-muted"></i>
                    <h5 className="mt-2">{t("pages.students.noStudentsFound")}</h5>
                  </div>
                )}
              </div>
            ) : (
            <div className="custom-datatable-filter table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    <th>{t("roles.STUDENT")}</th>
                    <th>{t("common.fields.matricule")}</th>
                    <th>{t("common.fields.class")}</th>
                    <th>{t("common.fields.level")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                    <th>{t("common.crud.actionColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : visibleProfiles.length > 0 ? (
                    visibleProfiles.map((profile) => {
                      const account = users.find((u) => u.id === profile.user_id);
                      const name =
                        [account?.first_name, account?.last_name].filter(Boolean).join(" ") ||
                        account?.email ||
                        profile.matricule;
                      return (
                      <tr key={profile.id}>
                        <td className="fw-medium">
                          <div className="d-flex align-items-center">
                            <span className="avatar avatar-sm flex-shrink-0 bg-primary-transparent rounded-circle d-flex align-items-center justify-content-center me-2">
                              {account?.avatar_url ? (
                                <img src={account.avatar_url} className="img-fluid rounded-circle" alt={name} />
                              ) : (
                                <span className="text-primary fw-semibold fs-12">
                                  {name.slice(0, 2).toUpperCase()}
                                </span>
                              )}
                            </span>
                            <span>{userLabel(profile.user_id)}</span>
                          </div>
                        </td>
                        <td>{profile.matricule}</td>
                        <td>{profile.class_id ? classLabel(profile.class_id) : "-"}</td>
                        <td>{profile.level_id ? levelLabel(profile.level_id) : "-"}</td>
                        <td>
                          <span className={`badge badge-soft-${statusMeta(profile.status).badge}`}>
                            {statusMeta(profile.status).label}
                          </span>
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              as="button"
                              className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0 i-false"
                            >
                              <i className="ti ti-dots-vertical fs-14"></i>
                            </Dropdown.Toggle>
                            <Dropdown.Menu align="end" className="p-3">
                              <Dropdown.Item as={Link} className="rounded-1" to={`/student/${profile.id}`}>
                                <i className="ti ti-eye me-2"></i>{t("common.crud.viewDetails")}
                              </Dropdown.Item>
                              {canUpdate && (
<Dropdown.Item className="rounded-1" onClick={() => openEditModal(profile)}>
                                <i className="ti ti-edit-circle me-2"></i>{t("common.crud.edit")}
                              </Dropdown.Item>
)}
                              {canDelete && (
<Dropdown.Item className="rounded-1" onClick={() => setDeleteTarget(profile)}>
                                <i className="ti ti-trash-x me-2"></i>{t("common.crud.delete")}
                              </Dropdown.Item>
)}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.students.noStudentsFound")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            )}

            <DataTablePagination
              variant="footer"
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onLimitChange={(v) => {
                setLimit(v);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <Modal show={showFormModal} onHide={closeFormModal} centered>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{formMode === "add" ? t("pages.students.addLabel") : t("pages.students.editTitle")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {submitError && <div className="alert alert-danger">{submitError}</div>}
            {formMode === "add" && (
              <>
                <div className="mb-3">
                  <div className="btn-group w-100" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${formData.accountMode === "new" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => handleFormChange("accountMode", "new")}
                    >
                      {t("common.crud.newAccount")}
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${formData.accountMode === "existing" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => handleFormChange("accountMode", "existing")}
                    >
                      {t("common.crud.existingAccount")}
                    </button>
                  </div>
                </div>

                {formData.accountMode === "new" ? (
                  <>
                    <div className="row">
                      <div className="col-sm-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {t("common.fields.firstName")} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.first_name ? "is-invalid" : ""}`}
                            value={formData.first_name}
                            onChange={(e) => handleFormChange("first_name", e.target.value)}
                          />
                          {formErrors.first_name && <div className="invalid-feedback">{formErrors.first_name}</div>}
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {t("common.fields.name")} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${formErrors.last_name ? "is-invalid" : ""}`}
                            value={formData.last_name}
                            onChange={(e) => handleFormChange("last_name", e.target.value)}
                          />
                          {formErrors.last_name && <div className="invalid-feedback">{formErrors.last_name}</div>}
                        </div>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        {t("common.fields.email")} <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className={`form-control ${formErrors.email ? "is-invalid" : ""}`}
                        value={formData.email}
                        onChange={(e) => handleFormChange("email", e.target.value)}
                      />
                      {formErrors.email && <div className="invalid-feedback">{formErrors.email}</div>}
                    </div>
                    <div className="row">
                      <div className="col-sm-6">
                        <div className="mb-3">
                          <label className="form-label">
                            {t("common.fields.password")} <span className="text-danger">*</span>
                          </label>
                          <input
                            type="password"
                            className={`form-control ${formErrors.password ? "is-invalid" : ""}`}
                            value={formData.password}
                            onChange={(e) => handleFormChange("password", e.target.value)}
                          />
                          {formErrors.password && <div className="invalid-feedback">{formErrors.password}</div>}
                        </div>
                      </div>
                      <div className="col-sm-6">
                        <div className="mb-3">
                          <label className="form-label">{t("common.fields.phone")}</label>
                          <input
                            type="text"
                            className="form-control"
                            value={formData.phone}
                            onChange={(e) => handleFormChange("phone", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label className="form-label">{t("common.ui.sexe")}</label>
                        <select
                          className="form-select"
                          value={formData.gender}
                          onChange={(e) => handleFormChange("gender", e.target.value)}
                        >
                          <option value="">-</option>
                          <option value="M">{t("common.ui.masculin")}</option>
                          <option value="F">{t("common.ui.feminin")}</option>
                        </select>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">{t("common.ui.dateNaissance")}</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.date_of_birth}
                          onChange={(e) => handleFormChange("date_of_birth", e.target.value)}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">{t("common.ui.lieuNaissance")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.place_of_birth}
                          onChange={(e) => handleFormChange("place_of_birth", e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t("common.ui.nniNumeroNationalIdentite")}</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.nni}
                          onChange={(e) => handleFormChange("nni", e.target.value)}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">{t("common.ui.nationalite")}</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder={t("common.ui.exMauritanienne")}
                          value={formData.nationality}
                          onChange={(e) => handleFormChange("nationality", e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="mb-3">
                    <label className="form-label">
                      {t("pages.students.existingAccountLabel")} <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${formErrors.user_id ? "is-invalid" : ""}`}
                      value={formData.user_id}
                      onChange={(e) => handleFormChange("user_id", e.target.value)}
                    >
                      <option value="">{t("common.crud.selectPlaceholder")}</option>
                      {usersWithoutProfile.map((u) => (
                        <option key={u.id} value={u.id}>
                          {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email} ({u.email})
                        </option>
                      ))}
                    </select>
                    {formErrors.user_id && <div className="invalid-feedback d-block">{formErrors.user_id}</div>}
                    {usersWithoutProfile.length === 0 && (
                      <div className="form-text">
                        {t("pages.students.noAvailableAccounts")}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            <div className="mb-3">
              <label className="form-label">
                {t("common.fields.matricule")} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${formErrors.matricule ? "is-invalid" : ""}`}
                value={formData.matricule}
                onChange={(e) => handleFormChange("matricule", e.target.value)}
              />
              {formErrors.matricule && <div className="invalid-feedback">{formErrors.matricule}</div>}
            </div>
            <div className="mb-3">
              <label className="form-label">{t("common.fields.class")}</label>
              <select
                className="form-select"
                value={formData.class_id}
                onChange={(e) => handleFormChange("class_id", e.target.value)}
              >
                <option value="">{t("common.fields.noneFem")}</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">{t("common.fields.level")}</label>
              <select
                className="form-select"
                value={formData.level_id}
                onChange={(e) => handleFormChange("level_id", e.target.value)}
              >
                <option value="">{t("common.fields.noneMasc")}</option>
                {levels.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">{t("common.crud.statusColumn")}</label>
              <select
                className="form-select"
                value={formData.status}
                onChange={(e) => handleFormChange("status", e.target.value)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <hr />
            <h6 className="fw-bold mb-2">{t("pages.students.langueAccessibilite")}</h6>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.students.languePreferee")}</label>
                <select
                  className="form-select"
                  value={formData.preferred_language}
                  onChange={(e) => handleFormChange("preferred_language", e.target.value)}
                >
                  <option value="fr">{t("pages.students.francais")}</option>
                  <option value="ar">{t("pages.students.arabe")}</option>
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.students.tempsSupplementaireMinutes")}</label>
                <input
                  type="number"
                  min="0"
                  max="600"
                  className="form-control"
                  value={formData.accessibility_extra_time_minutes}
                  onChange={(e) => handleFormChange("accessibility_extra_time_minutes", e.target.value)}
                />
                <div className="form-text">{t("pages.students.ajouteAutomatiquementDureeChaqueExamenSituation")}</div>
              </div>
            </div>

            <hr />
            <h6 className="fw-bold mb-2">{t("pages.students.parcoursAdministratif")}</h6>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.students.formation")}</label>
                <select
                  className="form-select"
                  value={formData.training_type}
                  onChange={(e) => handleFormChange("training_type", e.target.value)}
                >
                  <option value="">-</option>
                  <option value="academic">{t("pages.students.academique")}</option>
                  <option value="professional">{t("pages.students.professionnelle")}</option>
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">{t("pages.students.diplomeObtenuSiSortant")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("pages.students.exLicenceSciencesSante")}
                  value={formData.diploma_obtained}
                  onChange={(e) => handleFormChange("diploma_obtained", e.target.value)}
                />
              </div>
            </div>
            <div className="d-flex flex-wrap gap-4 mb-3">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="student-scholarship"
                  checked={formData.is_scholarship}
                  onChange={(e) => handleFormChange("is_scholarship", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="student-scholarship">
                  {t("pages.students.boursierBeneficiaireAide")}
                </label>
              </div>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="student-transferred"
                  checked={formData.is_transferred}
                  onChange={(e) => handleFormChange("is_transferred", e.target.checked)}
                />
                <label className="form-check-label" htmlFor="student-transferred">
                  {t("pages.students.transfereAutreEtablissement")}
                </label>
              </div>
            </div>
            {formData.is_transferred && (
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("pages.students.etablissementProvenance")}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.transfer_institution}
                    onChange={(e) => handleFormChange("transfer_institution", e.target.value)}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t("pages.students.date1ereInscriptionDansEtablissement")}</label>
                  <input
                    type="date"
                    className="form-control"
                    value={formData.transfer_first_enrollment_date}
                    onChange={(e) => handleFormChange("transfer_first_enrollment_date", e.target.value)}
                  />
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-light" onClick={closeFormModal} disabled={submitting}>
              {t("common.crud.cancel")}
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("common.crud.saving") : formMode === "add" ? t("common.crud.add") : t("common.crud.save")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={deleteTarget !== null} onHide={() => (deleting ? null : setDeleteTarget(null))} centered>
        <div className="modal-body text-center">
          <span className="delete-icon">
            <i className="ti ti-trash-x"></i>
          </span>
          <h4>{t("common.crud.confirmDeleteTitle")}</h4>
          <p>{t("pages.students.confirmDeleteBody")}</p>
          <div className="d-flex justify-content-center">
            <button type="button" className="btn btn-light me-3" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              {t("common.crud.cancel")}
            </button>
            <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
              {deleting ? t("common.crud.deleting") : t("common.crud.confirmDelete")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Students;
